import React from "react";

import { withStyles } from "@material-ui/core/styles";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ExpandLess from "@material-ui/icons/ExpandLess";
import FolderIcon from "@material-ui/icons/Folder";
import FolderOpenIcon from "@material-ui/icons/FolderOpen";
import Collapse from "@material-ui/core/Collapse";
import Drawer from "@material-ui/core/Drawer";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import ListItemText from "@material-ui/core/ListItemText";
import Typography from "@material-ui/core/Typography";

import { Link, withRouter } from "react-router-dom";

import AllureDockerDeleteFolderDialog from "../AllureDockerDeleteFolderDialog/AllureDockerDeleteFolderDialog";
import { DRAWER_WIDTH, EASE_SPRING } from "../../theme/tokens";
import { isAdmin } from "../../utility/user-actions";

// Одна константа на Bar и Drawer: раньше это были два независимых числа, и
// расхождение сдвигало контент ровно на разницу, съедая паддинг <main>.
const drawerWidth = DRAWER_WIDTH;
// Совпадает с серверной валидацией (tasks/nested-projects.md). Проверка на
// клиенте нужна только ради курсора и подсветки — сервер всё равно перепроверяет.
const MAX_PROJECT_DEPTH = 5;
const ROOT_DROP_TARGET = "";
const HOVER_EXPAND_DELAY_MS = 600;
// Откуда клон превью берёт фон: у самой строки он прозрачный, цвет даёт панель.
const DRAWER_PAPER_SELECTOR = ".MuiDrawer-paper";

const styles = (theme) => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1, 0, 2),
    ...theme.mixins.toolbar,
    justifyContent: "space-between",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerLabel: {
    color: theme.palette.text.disabled,
  },
  // Волосяная направляющая на каждый уровень вложенности — самое дешёвое, что
  // делает трёхуровневое дерево читаемым, и ровно в языке бренда.
  nestedList: {
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      bottom: 0,
      width: 1,
      backgroundColor: theme.palette.divider,
      left: "var(--kode-guide-left, 15px)",
    },
  },
  folderChevron: {
    color: theme.palette.text.disabled,
    transition: `transform 160ms ${EASE_SPRING}`,
  },
  folderChevronCollapsed: {
    transform: "rotate(-90deg)",
  },
  // Имя папки — пользовательские данные: сентенс-кейс, без UPPERCASE.
  folderLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  projectLabel: {
    fontSize: 13,
    fontWeight: 500,
  },
  // Строка проекта теперь сама является ссылкой, поэтому цвет и подчёркивание
  // надо гасить здесь — раньше это делал инлайновый стиль на <Link>.
  projectRow: {
    color: "inherit",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "none",
    },
  },
  // Выбранного проекта в дереве раньше вообще не было видно: selectProject
  // писал состояние, которое никто не читал.
  rowSelected: {
    backgroundColor: theme.palette.action.selected,
    boxShadow: `inset 2px 0 0 ${theme.palette.primary.main}`,
  },
  rowDragging: {
    opacity: 0.4,
  },
  dropInto: {
    outline: `1px solid ${theme.palette.primary.main}`,
    outlineOffset: -1,
    backgroundColor: theme.palette.action.hover,
  },
  dropInvalid: {
    outline: `1px solid ${theme.palette.error.main}`,
    outlineOffset: -1,
    cursor: "not-allowed",
  },
  rootDropZone: {
    opacity: 0.55,
  },
  rootDropZoneActive: {
    opacity: 1,
  },
  emptyFolderHint: {
    fontStyle: "italic",
  },
});

// Строит дерево из плоского словаря проектов и плоского списка папок.
// Ключи проектов — полные пути ("a/b/c"), папки приходят отдельно, потому что
// пустая папка не выводится ни из одного project_id.
// Узел: { name, projectId|null (задан только у листа-проекта), children }.
//
// Место в дереве и идентификатор проекта — разные вещи: id это имя, а папка
// приходит отдельным полем `folder`. Раньше id был путём и служил и тем, и
// другим, поэтому перенос проекта менял его id (и ломал пайплайны команд).
export const buildProjectTree = (projects, folders) => {
  const root = { name: "", projectId: null, children: {} };

  const ensureNode = (segments) => {
    let node = root;
    segments.forEach((segment) => {
      if (!node.children[segment]) {
        node.children[segment] = { name: segment, projectId: null, children: {} };
      }
      node = node.children[segment];
    });
    return node;
  };

  for (const projectId in projects) {
    const folderId = (projects[projectId] || {}).folder || "";
    const segments = folderId ? [...folderId.split("/"), projectId] : [projectId];
    ensureNode(segments).projectId = projectId;
  }
  (folders || []).forEach((folderId) => ensureNode(folderId.split("/")));
  return root;
};

const allureDockerProjectsSideDrawer = (props) => {
  const { classes } = props;
  const tree = buildProjectTree(props.projects || {}, props.folders);

  // По умолчанию раскрываем все группы — поведение близко к плоскому списку.
  const [collapsedGroups, setCollapsedGroups] = React.useState({});
  const [draggedProjectId, setDraggedProjectId] = React.useState(null);
  const [dropTargetPath, setDropTargetPath] = React.useState(null);
  const [folderPendingDeletion, setFolderPendingDeletion] = React.useState(null);
  const hoverExpandTimer = React.useRef(null);

  const toggleGroup = (groupPath) =>
    setCollapsedGroups((current) => ({
      ...current,
      [groupPath]: !current[groupPath],
    }));

  const cancelHoverExpand = () => {
    if (hoverExpandTimer.current) {
      clearTimeout(hoverExpandTimer.current);
      hoverExpandTimer.current = null;
    }
  };

  // Папка проекта приходит с сервера: из плоского имени её не вывести.
  const folderOf = (projectId) => ((props.projects || {})[projectId] || {}).folder || "";

  const isValidDropTarget = (targetPath) => {
    if (!isAdmin() || draggedProjectId === null || props.isMoving) {
      return false;
    }
    // Дроп в текущую папку — не перенос. Проверки «внутрь себя» больше нет:
    // проект не может быть папкой, а его имя не является префиксом пути.
    if (targetPath === folderOf(draggedProjectId)) {
      return false;
    }
    // Каталог проекта ложится на один уровень ниже папки — отсюда +1. Длину id
    // проверять больше нечего: перенос его не меняет.
    const targetDepth = targetPath === ROOT_DROP_TARGET ? 0 : targetPath.split("/").length;
    return targetDepth + 1 <= MAX_PROJECT_DEPTH;
  };

  const handleDragStart = (event, projectId) => {
    event.stopPropagation();
    // Без явного payload браузер утащит href ссылки как text/uri-list.
    event.dataTransfer.setData("text/plain", projectId);
    event.dataTransfer.effectAllowed = "move";
    // Превью задаём сами. Снимок «по умолчанию» браузер выбирает по своим
    // эвристикам и в дереве прихватывает соседние строки и кусок папки —
    // перетаскивание выглядит так, будто тащат всю группу.
    //
    // Снимок берётся с КЛОНА в body, а не с живой строки. Живая строка лежит
    // внутри `.MuiDrawer-paper` с `position: fixed`, и Chrome располагал её
    // снимок примерно на 126px ниже курсора — превью уезжало от мыши.
    // Проверено на стенде и отсечено по частям: аргументы setDragImage верны
    // (offsetX/offsetY ровно равны позиции курсора внутри строки),
    // трансформированных предков в цепочке нет, перерисовка от
    // setDraggedProjectId строку не двигает (сдвиг 0px). Оставался сам механизм
    // снятия живого узла из fixed-контекста — его и убираем: клон живёт прямо
    // в body, никакого fixed-предка над ним нет.
    //
    // currentTarget И ЕСТЬ строка: раньше это была внешняя ссылка, а строкой
    // был её firstElementChild. После схлопывания в один элемент
    // firstElementChild — это спан текста, и превью съёжилось бы до подписи.
    const row = event.currentTarget;
    if (row && typeof event.dataTransfer.setDragImage === "function") {
      const rect = row.getBoundingClientRect();
      const preview = row.cloneNode(true);
      preview.style.position = "fixed";
      // Уводим за левый край, а не прячем: display:none и visibility:hidden
      // дают пустой снимок — элемент обязан быть отрисован.
      preview.style.left = "-10000px";
      preview.style.top = "0";
      preview.style.width = `${rect.width}px`;
      preview.style.height = `${rect.height}px`;
      preview.style.pointerEvents = "none";
      // Фон у строки прозрачный, его даёт панель. В body панели над клоном нет,
      // и без явного фона превью вышло бы прозрачным.
      preview.style.backgroundColor = window.getComputedStyle(row.closest(DRAWER_PAPER_SELECTOR) || row)
        .backgroundColor;
      document.body.appendChild(preview);
      event.dataTransfer.setDragImage(preview, event.clientX - rect.left, event.clientY - rect.top);
      // Снимок браузер делает в конце dragstart, поэтому клон убираем не
      // сразу, а следующим тиком — иначе снимать будет нечего.
      window.setTimeout(() => {
        if (preview.parentNode) {
          preview.parentNode.removeChild(preview);
        }
      }, 0);
    }
    setDraggedProjectId(projectId);
  };

  const handleDragEnd = () => {
    cancelHoverExpand();
    setDraggedProjectId(null);
    setDropTargetPath(null);
  };

  const handleDragOver = (event, targetPath) => {
    if (!isValidDropTarget(targetPath)) {
      // preventDefault НЕ вызываем: браузер сам покажет курсор «нельзя»,
      // и onDrop не выстрелит — ни запроса, ни снека об очевидно плохом дропе.
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "move";
  };

  const handleDragEnter = (event, targetPath, isCollapsedGroup) => {
    event.stopPropagation();
    setDropTargetPath(targetPath);
    if (isCollapsedGroup && isValidDropTarget(targetPath)) {
      cancelHoverExpand();
      hoverExpandTimer.current = setTimeout(() => toggleGroup(targetPath), HOVER_EXPAND_DELAY_MS);
    }
  };

  const handleDragLeave = (event) => {
    event.stopPropagation();
    // dragleave прилетает не только когда курсор покинул строку, но и когда он
    // всего лишь зашёл на её ДОЧЕРНИЙ узел — иконку папки, подпись, шеврон.
    // Они закрывают почти всю площадь строки, поэтому безусловный сброс гасил
    // подсветку сразу после появления: цель подсвечивалась только в тонкой
    // полоске отступа, и со стороны выглядело, будто подсветки нет вовсе.
    // relatedTarget здесь — узел, В который курсор перешёл; если он внутри
    // строки, курсор её не покидал. Некоторые браузеры отдают null — тогда
    // считаем уход настоящим, как и раньше.
    if (event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    cancelHoverExpand();
    setDropTargetPath(null);
  };

  const handleDrop = (event, targetPath) => {
    event.preventDefault();
    event.stopPropagation();
    cancelHoverExpand();
    const projectId = event.dataTransfer.getData("text/plain") || draggedProjectId;
    setDraggedProjectId(null);
    setDropTargetPath(null);
    if (projectId) {
      props.moveProject(projectId, targetPath === ROOT_DROP_TARGET ? null : targetPath);
    }
  };

  const dropStateClass = (targetPath) => {
    if (dropTargetPath !== targetPath || draggedProjectId === null) {
      return undefined;
    }
    return isValidDropTarget(targetPath) ? classes.dropInto : classes.dropInvalid;
  };

  // Папки идут перед проектами: иначе пустая папка теряется между листьями.
  const sortedChildNames = (node) =>
    Object.keys(node.children).sort((leftName, rightName) => {
      const leftIsFolder = node.children[leftName].projectId === null;
      const rightIsFolder = node.children[rightName].projectId === null;
      if (leftIsFolder !== rightIsFolder) {
        return leftIsFolder ? -1 : 1;
      }
      return leftName.localeCompare(rightName);
    });

  const renderNodes = (node, parentPath, depth) =>
    sortedChildNames(node).map((childName) => {
      const child = node.children[childName];
      const groupPath = parentPath ? `${parentPath}/${childName}` : childName;
      const indentStyle = { paddingLeft: 8 + depth * 16 };

      if (child.projectId) {
        const isDragged = draggedProjectId === child.projectId;
        const isSelected = props.match.params.id === child.projectId;
        // Строка — ОДИН элемент, а не <Link> с вложенным <ListItem button>:
        // раньше фокусируемых узлов было два на строку (внешний <a href> и
        // внутренний div[role=button][tabindex=0]) — 21 таб-стоп на 12 строк.
        // role={undefined} попадает в `other`, который выигрывает мердж внутри
        // ButtonBase, и снимает role="button", ошибочно навешиваемый на ссылку.
        // Enter продолжает работать нативно: isNonNativeButton() смотрит на
        // tagName (A + href) и синтетический клик не добавляет.
        return (
          <ListItem
            button
            component={Link}
            to={`/projects/${child.projectId}`}
            key={child.projectId}
            role={undefined}
            id={child.projectId}
            style={indentStyle}
            className={`${classes.projectRow} ${isDragged ? classes.rowDragging : ""} ${
              isSelected ? classes.rowSelected : ""
            }`}
            draggable={isAdmin()}
            onDragStart={(event) => handleDragStart(event, child.projectId)}
            onDragEnd={handleDragEnd}
            onClick={() => {
              props.selectProject(child.projectId);
              // В узком режиме панель накрывает контент — оставлять её
              // открытой поверх только что выбранного проекта незачем.
              if (props.isNarrow) {
                props.handleSideDrawerClose();
              }
            }}
          >
            <ListItemText
              primary={child.name}
              primaryTypographyProps={{ className: classes.projectLabel }}
            />
          </ListItem>
        );
      }

      const isCollapsed = Boolean(collapsedGroups[groupPath]);
      const isEmptyFolder = Object.keys(child.children).length === 0;
      return (
        <React.Fragment key={groupPath}>
          <ListItem
            button
            style={indentStyle}
            className={dropStateClass(groupPath)}
            // Только у непустых: у пустой папки Collapse не рендерится, и
            // aria-expanded там был бы ложью.
            aria-expanded={isEmptyFolder ? undefined : !isCollapsed}
            aria-controls={isEmptyFolder ? undefined : `folder-${groupPath}`}
            onClick={() => toggleGroup(groupPath)}
            onDragOver={(event) => handleDragOver(event, groupPath)}
            onDragEnter={(event) => handleDragEnter(event, groupPath, isCollapsed)}
            onDragLeave={handleDragLeave}
            onDrop={(event) => handleDrop(event, groupPath)}
          >
            <ListItemIcon style={{ minWidth: 32 }}>
              <FolderIcon fontSize="small" className={classes.folderChevron} />
            </ListItemIcon>
            <ListItemText
              primary={child.name}
              primaryTypographyProps={{ className: classes.folderLabel }}
              secondary={isEmptyFolder ? "empty" : null}
              secondaryTypographyProps={{ className: classes.emptyFolderHint }}
            />
            {isEmptyFolder ? null : (
              <ExpandLess
                fontSize="small"
                className={`${classes.folderChevron} ${
                  isCollapsed ? classes.folderChevronCollapsed : ""
                }`}
              />
            )}
            {isEmptyFolder && (
              // IconButton обязан жить в ListItemSecondaryAction: голым внутри
              // <ListItem button> он даёт <button> внутри <button>.
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  size="small"
                  aria-label={`delete folder ${groupPath}`}
                  disabled={!isAdmin()}
                  onClick={() => setFolderPendingDeletion(groupPath)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            )}
          </ListItem>
          {!isEmptyFolder && (
            <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
              <List
                component="div"
                disablePadding
                id={`folder-${groupPath}`}
                className={classes.nestedList}
                style={{ "--kode-guide-left": `${15 + depth * 16}px` }}
              >
                {renderNodes(child, groupPath, depth + 1)}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      );
    });

  return (
    <Drawer
      // classes.drawer задаёт width: 272 КОРНЮ Drawer. У temporary корень —
      // это Modal, и подложка схлопнулась бы в полосу 272px, вместо того
      // чтобы накрыть экран. Поэтому в узком режиме класс не вешаем.
      className={props.isNarrow ? undefined : classes.drawer}
      variant={props.isNarrow ? "temporary" : "persistent"}
      anchor="left"
      open={props.isSideDrawerOpen}
      // Даёт Escape и клик по подложке даром; у persistent игнорируется.
      onClose={props.handleSideDrawerClose}
      ModalProps={{ keepMounted: true }}
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      <div className={classes.drawerHeader}>
        <Typography variant="overline" className={classes.headerLabel}>
          Projects
        </Typography>
        <IconButton onClick={props.handleSideDrawerClose} aria-label="collapse sidebar">
          {props.theme.direction === "rtl" ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </div>
      <List>
        <ListItem
          className={`${
            draggedProjectId === null ? classes.rootDropZone : classes.rootDropZoneActive
          } ${dropStateClass(ROOT_DROP_TARGET) || ""}`}
          style={{ paddingLeft: 8 }}
          onDragOver={(event) => handleDragOver(event, ROOT_DROP_TARGET)}
          onDragEnter={(event) => handleDragEnter(event, ROOT_DROP_TARGET, false)}
          onDragLeave={handleDragLeave}
          onDrop={(event) => handleDrop(event, ROOT_DROP_TARGET)}
        >
          <ListItemIcon style={{ minWidth: 32 }}>
            <FolderOpenIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="/ root level" />
        </ListItem>
        {renderNodes(tree, "", 0)}
      </List>
      <AllureDockerDeleteFolderDialog
        open={folderPendingDeletion !== null}
        folderId={folderPendingDeletion}
        closeDialog={() => setFolderPendingDeletion(null)}
        deleteFolder={props.deleteFolder}
        setAPIAlert={props.setAPIAlert}
      />
    </Drawer>
  );
};

export default withStyles(styles, { withTheme: true })(
  withRouter(allureDockerProjectsSideDrawer)
);
